import { useEffect, useRef, useState } from 'react';
import './PhotoCard.css';
import ImageWithLoader from '../ImageWithLoader/ImageWithLoader';
import { getImageSrc } from '../../utils/imageUtils';

export default function PhotoCard({ title, image_url, onPreview }) {
  const imageSrc = getImageSrc(image_url);
  const imageAlt = title || 'Event';
  const isPreviewable = Boolean(onPreview && imageSrc);
  const imageSrcSet = imageSrc ? `${imageSrc} 580w, ${imageSrc} 1160w` : undefined;
  const imageSizes = '(max-width: 700px) 92vw, 580px';
  const cardRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!imageSrc) {
      setShouldLoad(false);
      return undefined;
    }
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const node = cardRef.current;
    if (!node) {
      setShouldLoad(true);
      return undefined;
    }

    const root = node.closest('.HorizontalScroll__track') || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { root, rootMargin: '200px', threshold: 0.01 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [imageSrc]);

  const handlePreview = () => {
    if (!isPreviewable) return;
    onPreview(imageSrc, imageAlt);
  };

  const handleKeyDown = (event) => {
    if (!isPreviewable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePreview();
    }
  };

  return (
    <article className="PhotoCard" ref={cardRef}>
      {shouldLoad && imageSrc ? (
        <ImageWithLoader
          wrapperClassName={[
            'PhotoCard__imageWrap',
            isPreviewable ? 'PhotoCard__imageWrap--interactive' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          className="PhotoCard__image"
          src={imageSrc}
          alt={imageAlt}
          srcSet={imageSrcSet}
          sizes={imageSizes}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onClick={handlePreview}
          onKeyDown={handleKeyDown}
          role={isPreviewable ? 'button' : undefined}
          tabIndex={isPreviewable ? 0 : undefined}
        />
      ) : null}
      {title ? <h3 className="PhotoCard__title">{title}</h3> : null}
    </article>
  );
}
